import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Loader2, Mic, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTranscribe, useSaveDictationPos } from "@/lib/mutations";
import { useTranscribeConfig, useTranscribeStatus, useDictationPos } from "@/lib/queries";
import { useDictationRecorder, type DictationRecording, type DictationFormat } from "./useDictationRecorder";
import { insertTextAtCursor } from "./insertTextAtCursor";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { DictationModelMenu } from "./DictationModelMenu";

// Floating draggable dictation button. Records mic audio, transcribes it via
// /ai/transcribe (OpenRouter STT, model from app_settings), and inserts the
// text at the cursor position of the text field that was focused when the
// button was clicked.
//
// Shape (2026-10-13): a rectangle — mic icon + "Transcribe" label — 42px tall
// (75% of the 56px top nav bar height, per user request 2026-10-13). Width is
// content-driven, so the drag clamp measures the live element via btnRef
// (BUTTON_WIDTH_FALLBACK is the pre-mount estimate used by defaultPosition).
//
// Visual states (per spec):
//   idle         — maroon mic icon
//   initializing — red background + spinner (mic is initializing)
//   recording    — green background + pulsing + MM:SS timer
//   transcribing — maroon background + spinner
//
// Drag vs click: onPointerDown captures the pointer (setPointerCapture) and
// records the grab offset; if the pointer moves > 5px before pointerup it's
// a drag (reposition only, no state toggle — there is no onClick handler,
// the moved flag in onPointerUp is the click suppression), otherwise it's a
// click (toggle recording). Pointer capture is load-bearing: it keeps
// pointermove/up/cancel firing on the button even when the cursor leaves it,
// so a fast drag can't outrun the button and a missed pointerup can't leave
// the drag state stuck (hover moving the button with nothing held — the old
// flakiness). pointercancel restores the pre-drag position. onMouseDown
// preventDefault keeps focus in the text field — the button must never
// steal it, because the insertion target is whatever field was focused at
// click time.
//
// Position persistence (2026-10-09, migration 0021): the dragged position is
// stored per-user in the DB (dictation_button_pos via GET/PUT /dictation-pos)
// — NOT localStorage. The DB is the only source of truth so the position
// follows the user across browsers/machines. Initial state = the default
// bottom-right position; when the server position arrives it is applied ONCE
// (guarded by a ref) so a late refetch after the user drags can't yank the
// button back.
//
// Model health badge (2026-10-11): on mount the button probes the SAVED STT
// model via GET /ai/transcribe-status (1s silence WAV, 20s cap) and shows a
// green tick (available) / red X (down) / amber pulse (checking) badge at the
// icon's top-right. Right-click opens a searchable model switcher
// (DictationModelMenu) — gated on settings.view + settings.ai because picking
// a model PATCHes the clinic-wide /settings; the pick re-probes and updates
// the badge. Regular ai.use users (no settings perms) get the badge only.

const DRAG_THRESHOLD_PX = 5;
// Rectangle (2026-10-13): height 42px (75% of the 56px top nav bar height,
// reduced from 56px later the same day per user request); width is
// content-driven (icon + "Transcribe" label + padding). The clamp needs a
// width, so the live element is measured (btnRef) — this constant is only the
// pre-mount estimate for the default bottom-right position.
const BUTTON_SIZE = 42;
const BUTTON_WIDTH_FALLBACK = 128;

type Target = {
  el: HTMLTextAreaElement | HTMLInputElement;
  selectionStart: number;
  selectionEnd: number;
};

// In-flight drag state. grabOffset* keeps the grab point anchored (the button
// tracks the pointer from where it was grabbed — no jump-to-center); origin*
// is the pointercancel restore point; moved latches true once the pointer
// passes the drag threshold and suppresses the click-toggle on release.
type DragState = {
  pointerId: number;
  startX: number;
  startY: number;
  grabOffsetX: number;
  grabOffsetY: number;
  originX: number;
  originY: number;
  moved: boolean;
};

// Measured live width of the button (rectangle — content-driven width), with
// a pre-mount fallback for the first render / default position computation.
function clampPos(x: number, y: number, width: number = BUTTON_WIDTH_FALLBACK): { x: number; y: number } {
  return {
    x: Math.min(Math.max(x, 8), Math.max(8, window.innerWidth - width - 8)),
    y: Math.min(Math.max(y, 8), Math.max(8, window.innerHeight - BUTTON_SIZE - 8)),
  };
}

// Default position: bottom-right of the viewport with a 24px margin, clamped.
function defaultPosition(): { x: number; y: number } {
  return clampPos(
    window.innerWidth - BUTTON_WIDTH_FALLBACK - 24,
    window.innerHeight - BUTTON_SIZE - 24,
  );
}

function formatElapsed(sec: number): string {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = (sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export function DictationButton() {
  const { permissions, isAdmin } = useAuth();
  const transcribe = useTranscribe();
  // Only render for users with ai.use — the /ai/transcribe route requires it.
  // Admins bypass permission checks server-side (requirePerm role bypass) and
  // the backend returns an EMPTY permission set for admins, so the frontend
  // check must mirror the backend: isAdmin || permissions.has("ai.use").
  const allowed = isAdmin || permissions.has("ai.use");
  // Right-click model switcher: picking a model PATCHes /settings
  // { aiTranscribeModel }, which requires settings.view (route gate) +
  // settings.ai (field gate). Only offer the menu to users holding both.
  const canSwitchModel =
    isAdmin || (permissions.has("settings.view") && permissions.has("settings.ai"));
  // Model health probe (2026-10-11) — green tick / red X badge source.
  // Runs on mount for every ai.use user (the status route is auth-only).
  const { data: statusData, isFetching: statusFetching, error: statusError } = useTranscribeStatus(allowed);
  // Right-click menu open state. The menu (DictationModelMenu) mounts only
  // while open, so its inner queries fetch fresh data per open.
  const [menuOpen, setMenuOpen] = useState(false);
  // Dictation upload format (0020) — from GET /ai/transcribe-config (auth-only
  // endpoint, so non-admin users with ai.use get it too). Falls back to "wav"
  // while loading or on error — WAV works with every STT model.
  const { data: transcribeConfig } = useTranscribeConfig(allowed);
  const format: DictationFormat = transcribeConfig?.format ?? "wav";
  // One-time toast when the browser can't capture AAC natively and the
  // recorder silently substituted WAV (ref-stable callback; the hook fires
  // it at most once per recording).
  const onAacFallback = useCallback(() => {
    toast.message("AAC not supported here — using WAV");
  }, []);
  const { state, elapsed, start, stop, reset } = useDictationRecorder({ format, onAacFallback });
  // DB-backed position (migration 0021): start at the default bottom-right
  // position; apply the server position once when it arrives (below).
  const { data: savedPos } = useDictationPos(allowed);
  const savePos = useSaveDictationPos();
  const [pos, setPos] = useState<{ x: number; y: number }>(() => defaultPosition());
  const targetRef = useRef<Target | null>(null);
  const dragRef = useRef<DragState | null>(null);
  // The rendered button element — measured by clampPos for the width-aware
  // drag clamp (the rectangle's width is content-driven, not fixed).
  const btnRef = useRef<HTMLButtonElement | null>(null);
  // Latches true once the user drags (or the server position was applied) —
  // a late refetch after a user drag must NOT yank the button back to the
  // stale server position.
  const appliedServerPosRef = useRef(false);

  // Apply the server-saved position ONCE, only if the user hasn't dragged
  // since mount. x/y are null when the user has never dragged the button —
  // keep the default in that case.
  useEffect(() => {
    if (appliedServerPosRef.current) return;
    if (savedPos && savedPos.x !== null && savedPos.y !== null) {
      appliedServerPosRef.current = true;
      setPos(clampPos(savedPos.x, savedPos.y, btnRef.current?.offsetWidth));
    }
  }, [savedPos]);

  // Visual-only move during the drag (the DB write is deferred to
  // pointerup — no requests at 60Hz pointermove rates).
  const movePos = useCallback((x: number, y: number) => {
    setPos(clampPos(x, y, btnRef.current?.offsetWidth));
  }, []);

  const persistPos = useCallback((x: number, y: number) => {
    const clamped = clampPos(x, y, btnRef.current?.offsetWidth);
    setPos(clamped);
    appliedServerPosRef.current = true; // user intent wins over any late server position
    // Fire-and-forget DB save — the optimistic setPos above already shows
    // the final position; a failure only means it won't survive a reload.
    savePos.mutate({ x: Math.round(clamped.x), y: Math.round(clamped.y) });
  }, [savePos]);

  // Re-clamp on viewport resize so the button never ends up off-screen.
  useEffect(() => {
    const onResize = () => setPos((p) => clampPos(p.x, p.y, btnRef.current?.offsetWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Shared post-recording flow: transcribe the audio and insert the text at
  // the captured cursor. Used by BOTH the user-initiated stop and the 5-min
  // auto-stop. Always resets the recorder when done (success or error).
  const handleRecording = useCallback(
    async (recording: DictationRecording) => {
      try {
        const res = await transcribe.mutateAsync({ audio: recording.data, format: recording.format });
        if (res.message) {
          toast.error(res.message);
          return;
        }
        const text = res.text?.trim();
        if (!text) {
          toast.message("No speech detected");
          return;
        }
        const target = targetRef.current;
        if (!target || !document.body.contains(target.el)) {
          toast.error("Target field is no longer available");
          return;
        }
        insertTextAtCursor(target.el, text, target.selectionStart, target.selectionEnd);
        toast.success("Transcribed");
      } catch (e) {
        const err = e as Error & { body?: { message?: unknown; error?: unknown } };
        // body.message may be an object (zod-validator 400s serialize ZodError
        // as {name, message}) — never render a non-string as a toast child.
        const msg =
          typeof err?.body?.message === "string"
            ? err.body.message
            : err?.body?.message && typeof err.body.message === "object"
              ? String((err.body.message as { message?: unknown }).message ?? "")
              : err.message;
        toast.error(msg || "Transcription failed");
      } finally {
        reset();
      }
    },
    [transcribe, reset],
  );

  const onToggle = useCallback(async () => {
    if (state === "initializing" || state === "transcribing") return;

    if (state === "idle") {
      // Focus capture: the click must have happened while a text field was
      // focused. onMouseDown preventDefault keeps the field focused through
      // the click, so document.activeElement is still the target field.
      const el = document.activeElement;
      const ok =
        (el instanceof HTMLTextAreaElement ||
          (el instanceof HTMLInputElement &&
            !["number", "date", "checkbox", "radio", "file", "range"].includes(el.type))) &&
        !el.disabled &&
        !el.readOnly;
      if (!ok) {
        toast.error("Tap into a text field first");
        return;
      }
      targetRef.current = {
        el: el as HTMLTextAreaElement | HTMLInputElement,
        selectionStart: (el as HTMLTextAreaElement | HTMLInputElement).selectionStart ?? (el as HTMLTextAreaElement | HTMLInputElement).value.length,
        selectionEnd: (el as HTMLTextAreaElement | HTMLInputElement).selectionEnd ?? (el as HTMLTextAreaElement | HTMLInputElement).value.length,
      };
      try {
        await start({ onAutoStop: handleRecording });
      } catch (e) {
        toast.error((e as Error).message);
      }
      return;
    }

    if (state === "recording") {
      try {
        const recording = await stop();
        await handleRecording(recording);
      } catch (e) {
        // stop() rejects only if not recording (double-tap race) — the
        // transcribe path handles its own errors inside handleRecording.
        toast.error((e as Error).message);
        reset();
      }
    }
  }, [state, start, stop, reset, handleRecording]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0 && e.pointerType === "mouse") return; // primary press only
    // A second concurrent pointer (multi-touch) must not hijack the active drag.
    if (dragRef.current && dragRef.current.pointerId !== e.pointerId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const d: DragState = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      grabOffsetX: e.clientX - rect.left,
      grabOffsetY: e.clientY - rect.top,
      originX: pos.x,
      originY: pos.y,
      moved: false,
    };
    dragRef.current = d;
    // Capture so pointermove/up/cancel keep firing on the button even when
    // the cursor leaves it (fast drags outrun the 56px hit area). Guarded:
    // synthetic/dispatched events (test harnesses, some browser edge cases)
    // have no active OS pointer and setPointerCapture throws NotFoundError —
    // the drag still works, capture is only an optimization for real pointers.
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // no active pointer for this id — proceed without capture
    }
  }, [pos.x, pos.y]);

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (!d.moved) {
        if (Math.hypot(dx, dy) <= DRAG_THRESHOLD_PX) return;
        d.moved = true;
      }
      // Track the pointer, keeping the grab point under it (no jump to center).
      movePos(e.clientX - d.grabOffsetX, e.clientY - d.grabOffsetY);
    },
    [movePos],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return; // ignore stray pointers
      dragRef.current = null;
      if (d.moved) {
        // Drag finished — persist the dropped position; moved=true suppresses
        // the click-toggle (there is no onClick; the toggle lives here).
        persistPos(e.clientX - d.grabOffsetX, e.clientY - d.grabOffsetY);
        return;
      }
      void onToggle(); // press+release without meaningful movement = click
    },
    [onToggle, persistPos],
  );

  // Pointercancel (browser interrupts the gesture — e.g. palm rejection,
  // touch taking over): abort the drag and restore the pre-drag position.
  // Chosen over drop-in-place so an interrupted gesture never half-moves the
  // button, and because a cancelled gesture is not a user intent to move it.
  const onPointerCancel = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return; // ignore stray pointers
      dragRef.current = null;
      if (d.moved) movePos(d.originX, d.originY);
    },
    [movePos],
  );

  if (!allowed) return null;

  // Visuals per state.
  const bg =
    state === "initializing"
      ? "bg-red-600"
      : state === "recording"
        ? "bg-green-600 animate-pulse"
        : state === "transcribing"
          ? "bg-maroon-600 opacity-80"
          : "bg-maroon-600 hover:bg-maroon-700";
  const label =
    state === "initializing"
      ? "Mic initializing…"
      : state === "recording"
        ? `Recording ${formatElapsed(elapsed)} — tap to stop`
        : state === "transcribing"
          ? "Transcribing…"
          : "Dictate";

  // Health badge (2026-10-11): amber pulse while probing (first load or the
  // re-probe after a model switch — the shared query refetches), green tick
  // when the saved model answered, red X when it didn't. A network error on
  // the probe itself (not a model failure) shows no badge — the status is
  // genuinely unknown, not "down".
  const badge = statusFetching
    ? "checking"
    : statusError
      ? "none"
      : statusData?.ok
        ? "ok"
        : statusData
          ? "down"
          : "checking";
  const badgeTitle =
    badge === "ok"
      ? `Transcription model available: ${statusData?.model}`
      : badge === "down"
        ? `Transcription model unavailable: ${statusData?.model}${statusData?.message ? ` (${statusData.message})` : ""}`
        : "Checking transcription model…";
  const badgeAria =
    badge === "ok"
      ? "Transcription model available"
      : badge === "down"
        ? "Transcription model unavailable"
        : "Checking transcription model";

  return createPortal(
    <Popover open={menuOpen} onOpenChange={setMenuOpen}>
      {/* PopoverAnchor instead of PopoverTrigger: the button must keep its
          pointer/drag handlers and must NOT be an accessibility trigger —
          right-click (contextmenu) opens the menu, not a keyboard/click
          activation. Anchor positions the floating content at the button. */}
      <PopoverAnchor asChild>
        <button
          ref={btnRef}
          type="button"
          aria-label={label}
          title={canSwitchModel ? `${label} — right-click to switch model` : label}
          // z-[60]: above Dialog/Sheet overlays + content (z-50) so the mic stays
          // clickable and undimmed while a modal is open. The user may be typing
          // INTO a dialog's textarea when dictating — the button must float above
          // the modal layer, not under its overlay.
          // pointer-events-auto: react-remove-scroll (Radix's scroll lock) sets
          // body { pointer-events: none } while a modal is open and re-enables
          // it only INSIDE the dialog portal — a body child outside the portal
          // would be click-dead regardless of z-index. Explicit auto keeps the
          // mic interactive over the modal.
          // Shape (2026-10-13): rectangle (rounded-lg — the app's button
          // radius), h-[42px] = 75% of the Topbar's 56px height (reduced
          // from h-14 the same day per user request); width is content-driven
          // (icon + "Transcribe" label + px-4 padding).
          className={`fixed z-[60] pointer-events-auto flex h-[42px] select-none items-center justify-center gap-2 rounded-lg px-4 text-cream-50 shadow-lg transition-colors ${bg}`}
          style={{ left: pos.x, top: pos.y, touchAction: "none" }}
          onMouseDown={(e) => e.preventDefault()} // never steal focus from the text field
          onPointerDown={(e) => {
            // Stop the event BEFORE React's synthetic system re-emits it at the
            // document: Radix's DismissableLayer listens for pointerdown on the
            // document (bubble phase) and would treat this press as an
            // outside-click, closing the open Dialog/Sheet. The mic press must
            // stay entirely local to the button.
            e.stopPropagation();
            onPointerDown(e);
          }}
          onPointerMove={onPointerMove}
          onPointerUp={(e) => {
            // Same containment for release: a pointerup/click pair that leaks to
            // the document can also dismiss some Radix layers.
            e.stopPropagation();
            onPointerUp(e);
          }}
          onPointerCancel={onPointerCancel}
          onContextMenu={(e) => {
            // Right-click → searchable model switcher (2026-10-11). Suppress
            // the native context menu and stop propagation so nothing else
            // (e.g. a Radix layer's outside-pointerdown handling) reacts.
            // Left-drag still works: contextmenu fires after pointerup, so the
            // drag has already completed (and persisted) by the time we open.
            if (!canSwitchModel) return; // no perms → native menu as before
            e.preventDefault();
            e.stopPropagation();
            setMenuOpen(true);
          }}
        >
          {state === "initializing" || state === "transcribing" ? (
            <Loader2 size={22} className="animate-spin" />
          ) : (
            <Mic size={22} />
          )}
          <span className="text-sm font-medium">Transcribe</span>
          {state === "recording" && (
            <span className="absolute -top-7 left-1/2 -translate-x-1/2 rounded bg-dark-800 px-1.5 py-0.5 text-[11px] font-medium text-cream-50 tabular-nums">
              {formatElapsed(elapsed)}
            </span>
          )}
          {/* Health badge — top-right corner, above the icon. pointer-events-none
              so it never intercepts the button's pointer handlers. */}
          {badge !== "none" && (
            <span
              aria-label={badgeAria}
              title={badgeTitle}
              className={`pointer-events-none absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-cream-50 shadow ${
                badge === "ok"
                  ? "bg-green-600"
                  : badge === "down"
                    ? "bg-red-600"
                    : "animate-pulse bg-amber-500"
              }`}
            >
              {badge === "ok" ? (
                <Check size={12} strokeWidth={3} className="text-white" />
              ) : badge === "down" ? (
                <X size={12} strokeWidth={3} className="text-white" />
              ) : (
                <span className="h-1.5 w-1.5 rounded-full bg-white" />
              )}
            </span>
          )}
        </button>
      </PopoverAnchor>
      {/* Right-click model switcher. z-[70] — above the button's z-[60] so the
          menu floats over the badge; pointer-events-auto — portaled to body,
          so a Radix modal's scroll lock (body pointer-events:none) would
          otherwise click-dead it (same reason as the button itself).
          collisionPadding keeps it on screen when the button sits at a
          viewport edge. */}
      {canSwitchModel && (
        <PopoverContent align="end" sideOffset={8} collisionPadding={12} className="z-[70] pointer-events-auto w-80 p-0">
          <DictationModelMenu />
        </PopoverContent>
      )}
    </Popover>,
    document.body,
  );
}
